/**
 * Response Plan rules shared by the Worker and the app (Rod, 2026-11-04).
 * The plan is native portal screens backed by the database. Everything here
 * is pure, so the Worker DTOs, the tab and the tests use the same section
 * groups, limits, chip names and task filters.
 *
 * scripts/upsert-response-plan.mjs has twins of PLAN_SECTION_KEY, PLAN_LIMITS
 * and the chip names. Change them together.
 */
import type { PlanChip, PlanLink, PlanListItem, PlanMilestone, PlanSection, PlanSectionKind, PlanTask } from '../types'

/** A plan section or task key. The database uses the same rule. */
export const PLAN_SECTION_KEY = /^[a-z0-9][a-z0-9-]{0,79}$/
/** The longest note or reply a person can send. The Worker uses the same limit. */
export const PLAN_NOTE_MAX_CHARS = 2_000
export const THREAD_REPLY_MAX_CHARS = 2_000
/** The longest corrected Prentus Assistant answer. The database allows 4,000 characters per reply. */
export const ASSISTANT_CORRECTION_MAX_CHARS = 4_000

export const PLAN_LIMITS = {
  sections: 60,
  tasks: 200,
  titleChars: 200,
  taskTitleChars: 300,
  textChars: 2_000,
  listItems: 60,
  specChars: 20_000,
} as const

export const PLAN_CHIPS: readonly PlanChip[] = ['done', 'prog', 'beta', 'plan', 'due']
export const PLAN_SECTION_KINDS: readonly PlanSectionKind[] = ['timeline', 'tasks', 'card', 'feedback', 'also']

export function isPlanChip(value: unknown): value is PlanChip {
  return typeof value === 'string' && (PLAN_CHIPS as readonly string[]).includes(value)
}

export function isPlanSectionKind(value: unknown): value is PlanSectionKind {
  return typeof value === 'string' && (PLAN_SECTION_KINDS as readonly string[]).includes(value)
}

/** The section nav: one group per kind of section, in this order. Empty groups are left out. */
export const PLAN_GROUPS: ReadonlyArray<{ id: string; label: string; kinds: readonly PlanSectionKind[] }> = [
  { id: 'timeline', label: 'Timeline', kinds: ['timeline'] },
  { id: 'tasks', label: 'Tasks', kinds: ['tasks'] },
  { id: 'shipping', label: 'What We\'re Shipping', kinds: ['card'] },
  { id: 'feedback', label: 'Feedback', kinds: ['feedback'] },
  { id: 'also', label: 'Also on Our List', kinds: ['also'] },
]

export function planGroups(sections: PlanSection[]): Array<{ id: string; label: string; sections: PlanSection[] }> {
  return PLAN_GROUPS
    .map((group) => ({ id: group.id, label: group.label, sections: sections.filter((section) => group.kinds.includes(section.kind)) }))
    .filter((group) => group.sections.length > 0)
}

export type TaskStatusFilter = 'open' | 'done' | 'all'
export type TaskOwnerFilter = 'all' | 'prentus' | 'customer'

export function filterTasks(tasks: PlanTask[], status: TaskStatusFilter, owner: TaskOwnerFilter): PlanTask[] {
  return tasks.filter((task) => (status === 'all' || (status === 'done') === task.done) && (owner === 'all' || task.owner === owner))
}

// --- Reading stored section content -----------------------------------------
// The content column is JSON that a script writes. The Worker reads it
// defensively: a wrong field type becomes an empty value, never an error.

const str = (value: unknown, max: number = PLAN_LIMITS.textChars): string => (typeof value === 'string' ? value.trim().slice(0, max) : '')
const list = (value: unknown): unknown[] => (Array.isArray(value) ? value.slice(0, PLAN_LIMITS.listItems) : [])

/** Only https links (and the portal's own relative links) reach the page. */
export function safeUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  if (!trimmed || trimmed.length > 500) return null
  try {
    const url = new URL(trimmed)
    return url.protocol === 'https:' ? url.toString() : null
  } catch {
    return null
  }
}

function link(value: unknown): PlanLink | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const url = safeUrl(record.url)
  const label = str(record.label, 120)
  return url && label ? { url, label } : null
}

function listItem(value: unknown): PlanListItem | null {
  if (typeof value === 'string') return value.trim() ? { text: value.trim().slice(0, PLAN_LIMITS.textChars), chip: 'plan', chipText: 'Planned' } : null
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const text = str(record.text)
  if (!text) return null
  return { text, chip: isPlanChip(record.chip) ? record.chip : 'plan', chipText: str(record.chipText, 60) || 'Planned' }
}

function milestone(value: unknown): PlanMilestone | null {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const title = str(record.title, PLAN_LIMITS.titleChars)
  if (!title) return null
  const state = record.state === 'done' || record.state === 'next' ? record.state : 'later'
  return { date: str(record.date, 60), title, state, text: str(record.text) }
}

/** A section DTO from its stored row. */
export function planSectionFromRow(row: { section_key: string; kind: string; title: string; content: unknown } | Record<string, any>): PlanSection | null {
  if (!isPlanSectionKind(row.kind)) return null
  const content = (row.content && typeof row.content === 'object' ? row.content : {}) as Record<string, unknown>
  const base = { key: row.section_key, title: row.title, intro: str(content.intro) }
  switch (row.kind) {
    case 'timeline':
      return {
        ...base,
        kind: 'timeline',
        stats: list(content.stats).flatMap((stat) => {
          const record = (stat && typeof stat === 'object' ? stat : {}) as Record<string, unknown>
          const value = str(record.value, 60)
          return value ? [{ value, label: str(record.label, 300) }] : []
        }),
        milestones: list(content.milestones).map(milestone).filter((entry): entry is PlanMilestone => Boolean(entry)),
        fixedTitle: str(content.fixedTitle, 120) || 'Recently Fixed',
        fixed: list(content.fixed).flatMap((entry) => {
          const record = (entry && typeof entry === 'object' ? entry : {}) as Record<string, unknown>
          const text = str(record.text)
          return text ? [{ text, when: str(record.when, 60) }] : []
        }),
      }
    case 'tasks':
      return { ...base, kind: 'tasks', link: link(content.link) }
    case 'card':
      return {
        ...base,
        kind: 'card',
        what: str(content.what),
        chip: isPlanChip(content.chip) ? content.chip : 'prog',
        chipText: str(content.chipText, 60),
        timing: str(content.timing, 300),
        solves: list(content.solves).map((entry) => str(entry)).filter(Boolean),
        prototype: link(content.prototype),
        tryIt: str(content.tryIt),
      }
    case 'feedback':
    case 'also':
      return { ...base, kind: row.kind, items: list(content.items).map(listItem).filter((entry): entry is PlanListItem => Boolean(entry)) }
  }
}

/** Plain text of a section, for the Prentus Assistant's sources. */
export function planSectionText(section: PlanSection): string {
  const lines = [section.title, section.intro]
  if (section.kind === 'timeline') {
    lines.push(...section.stats.map((stat) => `${stat.value}: ${stat.label}`))
    lines.push(...section.milestones.map((entry) => `${entry.date} (${entry.state === 'done' ? 'done' : entry.state === 'next' ? 'next' : 'coming'}): ${entry.title}. ${entry.text}`))
    if (section.fixed.length) lines.push(`${section.fixedTitle}:`, ...section.fixed.map((entry) => `- ${entry.text} (${entry.when})`))
  }
  if (section.kind === 'card') {
    lines.push(section.what, `Status: ${section.chipText}. ${section.timing}`, 'What it solves:', ...section.solves.map((entry) => `- ${entry}`))
    if (section.tryIt) lines.push(section.tryIt)
  }
  if (section.kind === 'feedback' || section.kind === 'also') lines.push(...section.items.map((entry) => `- ${entry.text} (${entry.chipText})`))
  return lines.filter(Boolean).join('\n')
}
